import { useState, useEffect, useRef, Fragment } from 'react';
import { api } from '../../api';
import { useIsMobile, formatCron, formatDateTime, formatBytes, DEFAULT_TZ } from '../../utils';
import { JobModal } from './JobModal';
import { ConfirmModal } from '../Modals';
import { Badge } from './Badge';
import JobHoneypotProtection from '../JobHoneypotProtection';

// #192 D3, 2.b — avance de una carga inicial en curso, debajo del job. Sólo
// se muestra con la carga en pending/in_progress (done/off: nada). Prioridad
// estricta: en pausa primero (el job desactivado es lo único que importa,
// aunque haya datos), después sin datos (ningún tramo cerró todavía),
// después el progreso real — nunca "—"/"NaN"/"0%" en el lugar de un dato
// que falta.
// #198 parte 2 — el PUT del backend combina con lo guardado, así que el
// interruptor sólo manda lo que JobIn exige (server_id, name, job_type) más
// el campo que cambia. Mandar el objeto de la lista (`{...job}`) incluía los
// secretos enmascarados ("********") y los pisaba.
// #213 punto 7 — con backups guardados, el modal avisa antes de confirmar: el
// borrado quita las corridas del panel, pero los snapshots siguen en el repo.
export function deleteJobMessage(job) {
  const n = job.backups_count || 0;
  const pausar = 'Si sólo querés que deje de correr, pausalo con el interruptor: se conserva todo y lo podés reactivar cuando quieras.';
  if (n > 0) {
    return `Este job tiene ${n} ${n === 1 ? 'backup guardado' : 'backups guardados'}. Al borrarlo dejan de verse en el panel; se conservan según la retención del plan y sólo se recuperan a mano con el kit de recuperación. ${pausar} ¿Eliminar el job "${job.name}"?`;
  }
  return `¿Eliminar el job "${job.name}"? Esta acción no se puede deshacer. ${pausar}`;
}

export function toggleJobPayload(job) {
  return { server_id: job.server_id, name: job.name, job_type: job.job_type, enabled: !job.enabled };
}

export function InitialLoadProgress({ j }) {
  if (!['pending', 'in_progress'].includes(j.initial_load_state)) return null;

  const total = j.initial_load_bytes_total;
  const done  = j.initial_load_bytes_done || 0;

  if (j.initial_load_paused) {
    const pct = total ? Math.round((done / total) * 100) : null;
    return (
      <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(255,209,102,0.08)', border: '1px solid var(--yellow)', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--yellow)' }}>
        Carga inicial en pausa: el job está desactivado.{pct !== null ? ` ${pct}% completado.` : ''}
      </div>
    );
  }

  if (!total) {
    return (
      <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>
        Carga inicial: esperando que se complete el primer tramo.
      </div>
    );
  }

  const pct = Math.min(100, Math.round((done / total) * 100));
  const dias = j.initial_load_dias_estimados;
  const eta = dias != null ? `, fin estimado: hasta ${dias} día${dias === 1 ? '' : 's'}` : '';
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)' }} />
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
        Carga inicial: {pct}% — {formatBytes(done)} de {formatBytes(total)}{eta}
      </div>
    </div>
  );
}

// #133 — detalle de exclusiones activas, visible para el rol client: un backup
// que omite archivos en silencio es su propio problema.
function ExclusionsDetail({ job }) {
  const excludes = job.restic_excludes || [];
  return (
    <div style={{ padding: '14px 16px', background: 'var(--bg)', fontFamily: 'var(--mono)', fontSize: 12 }}>
      <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Exclusiones</div>
      {excludes.length === 0 ? (
        <div style={{ color: 'var(--muted)' }}>Sin exclusiones: se respalda todo el contenido de las rutas configuradas.</div>
      ) : (
        <>
          <ul style={{ margin: '0 0 8px 18px', padding: 0, color: 'var(--text)' }}>
            {excludes.map((p, i) => <li key={i} style={{ marginBottom: 2 }}>{p}</li>)}
          </ul>
          <div style={{ color: 'var(--muted)', fontSize: 11 }}>Los archivos que coincidan con estos patrones no se incluyen en el backup.</div>
        </>
      )}
    </div>
  );
}

// #207 parte 2 — la lista se cargaba una sola vez, así que el botón «Rearmar
// honeypots» seguía habilitado durante una corrida hasta recargar la página.
// Mientras algún job corre (o hasta WATCH_AFTER_TRIGGER_MS después de lanzar uno,
// que es lo que tarda el agente en abrir el run) se refresca en silencio.
const RUNNING_REFRESH_MS = 5000;
const WATCH_AFTER_TRIGGER_MS = 60000;

// #107 — lo que ve quien no gestiona jobs: estado, nombre, tipo, horario y retención; nada de cómo está armado.
function JobSummaryModal({ job, onClose }) {
  const rows = [['Nombre', job.name], ['Servidor', job._serverName], ['Tipo', (job.job_type || '').toUpperCase()],
                ['Schedule', formatCron(job.schedule)], ['Retención', job.retention_days ? `${job.retention_days} días` : '—'],
                ['Estado', job.enabled ? 'Habilitado' : 'Pausado']];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: 16, boxSizing: 'border-box' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>VER JOB</div>
        <div style={{ padding: 24 }}>
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{k}</span>
              <span style={{ textAlign: 'right' }}>{v || '—'}</span>
            </div>
          ))}
          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
            La configuración del backup (rutas, bases, cuentas de la nube) no está disponible con tu permiso.
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CERRAR</button>
        </div>
      </div>
    </div>
  );
}

// canWrite (#210 etapa 2): crear/editar/borrar/pausar; sin él, la pestaña es de sólo lectura (ver, ejecutar a mano).
// hideConfig (#107): el backend no manda la configuración (rutas, bases, ids de la nube...); se ve un resumen.
export function JobsTab({ clientId, servers, canWrite = false, hideConfig = false }) {
  const isMobile              = useIsMobile();
  const [jobs, setJobs]       = useState([]);
  const [modal, setModal]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState({ msg: '', type: '' });
  const [triggering, setTriggering] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const watchUntil = useRef(0);
  const jobsRef = useRef([]);
  const loadRef = useRef(null);

  const showToast = (msg, type = 'error') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 3000); };

  // silent: refrescar sin el «CARGANDO…» a pantalla completa, que desmonta las
  // filas (y con ellas el resultado del rearmado de honeypots, #207).
  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const allJobs = await api.getJobsByClient(clientId);
      const serverMap = Object.fromEntries(servers.map(s => [s.id, s.hostname]));
      allJobs.forEach(j => { j._serverName = serverMap[j.server_id] || j.server_id; });
      setJobs(allJobs);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [clientId]);

  // siempre la versión más nueva de jobs/load (el intervalo se crea una sola vez)
  jobsRef.current = jobs;
  loadRef.current = load;
  useEffect(() => {
    const t = setInterval(() => {
      if (jobsRef.current.some(j => j.job_running) || Date.now() < watchUntil.current) loadRef.current(true);
    }, RUNNING_REFRESH_MS);
    return () => clearInterval(t);
  }, [clientId]);

  const handleDelete = async (id) => {
    try { await api.deleteJob(id); showToast('Job eliminado', 'ok'); load(); } catch (e) { showToast(e.message); }
  };

  const handleTrigger = async (id) => {
    setTriggering(id);
    try {
      const res = await api.triggerJob(id);
      watchUntil.current = Date.now() + WATCH_AFTER_TRIGGER_MS;
      if (res && res.agent_online === false) {
        showToast('Job encolado — se ejecutará cuando el agente reconecte', 'warning');
      } else if (res && res.delivered === true) {
        showToast('Job ejecutándose...', 'ok');
      } else {
        showToast('Job encolado. Se ejecutará en breve.', 'warning');
      }
    } catch (e) { showToast(e.message); }
    finally { setTriggering(null); }
  };

  const handleToggle = async (job) => {
    try { await api.updateJob(job.id, toggleJobPayload(job)); showToast(job.enabled ? 'Job deshabilitado' : 'Job habilitado', 'ok'); load(); }
    catch (e) { showToast(e.message); }
  };

  // Etiqueta de resumen para la columna Paths / DB
  const jobSummary = (j) => {
    if (hideConfig) return '—';
    if (j.job_type === 'files') return (j.paths || []).join(', ') || '—';
    const dbLabel = j.db_name || (j.job_type === 'oracle' ? 'FULL' : 'todas');
    const hostLabel = j.db_host && j.db_host !== 'localhost' ? `@${j.db_host}` : '';
    return `${dbLabel}${hostLabel}`;
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;

  return (
    <div>
      {toast.msg && (
        <div style={{ marginBottom: 12, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12,
          background: toast.type === 'ok' ? 'rgba(0,229,160,0.1)' : toast.type === 'warning' ? 'rgba(255,193,7,0.1)' : 'rgba(255,77,109,0.1)',
          border: `1px solid ${toast.type === 'ok' ? 'var(--green)' : toast.type === 'warning' ? '#ffc107' : 'var(--red)'}`,
          color: toast.type === 'ok' ? 'var(--green)' : toast.type === 'warning' ? '#ffc107' : 'var(--red)' }}>
          {toast.type === 'ok' ? '✓' : toast.type === 'warning' ? '⚠' : '✕'} {toast.msg}
        </div>
      )}
      {modal !== null && hideConfig && <JobSummaryModal job={modal} onClose={() => setModal(null)} />}
      {modal !== null && !hideConfig && <JobModal readOnly={!canWrite} job={modal === 'new' ? null : modal} servers={servers} onClose={() => setModal(null)} onSaved={() => { load(); showToast(modal?.id ? 'Job actualizado' : 'Job creado', 'ok'); }} />}
      {confirmDelete && <ConfirmModal title="Eliminar job" message={deleteJobMessage(confirmDelete)} danger onConfirm={() => { handleDelete(confirmDelete.id); setConfirmDelete(null); }} onCancel={() => setConfirmDelete(null)} />}
      {canWrite && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button onClick={() => setModal('new')} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>+ NUEVO JOB</button>
        </div>
      )}
      {jobs.length === 0 ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
          Sin jobs. Agregá un servidor primero y luego creá un job.
        </div>
      ) : isMobile ? (
        <div>
          {jobs.map(j => (
            <div key={j.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>JOB</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{j.name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>{j._serverName} · {(j.job_type||'').toUpperCase()}</div>
              <div style={{ marginBottom: 10 }}><JobHoneypotProtection jobType={j.job_type} status={j.honeypot_status} jobId={j.id} agentVersion={servers.find(s => s.id === j.server_id)?.agent_version} running={j.job_running} onChanged={() => load(true)} /></div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>SCHEDULE</div>
              <div style={{ fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--muted)', marginBottom: 10 }}>{formatCron(j.schedule)}</div>
              {j.last_run_at && (
                <>
                  <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>ÚLTIMO RUN</div>
                  <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)', marginBottom: 6 }}>{formatDateTime(j.last_run_at, servers.find(s => s.id === j.server_id)?.timezone || DEFAULT_TZ)}</div>
                  <Badge s={j.last_run_status} />
                </>
              )}
              <InitialLoadProgress j={j} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
                <div onClick={canWrite ? () => handleToggle(j) : undefined} title={canWrite ? undefined : 'Lo gestiona tu proveedor'} style={{ width: 36, height: 20, borderRadius: 10, cursor: canWrite ? 'pointer' : 'default', opacity: canWrite ? 1 : 0.6, background: j.enabled ? 'var(--accent)' : 'var(--border)', position: 'relative', display: 'inline-block' }}>
                  <div style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: 'white', left: j.enabled ? 18 : 2, transition: 'left 0.2s' }} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => handleTrigger(j.id)} disabled={triggering === j.id} style={{ background: 'transparent', border: '1px solid var(--green)', color: 'var(--green)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', opacity: triggering === j.id ? 0.5 : 1 }}>
                    {triggering === j.id ? '...' : '▶'}
                  </button>
                  {j.job_type !== 'integrity_check' && !hideConfig && (
                    <button onClick={() => setExpandedId(id => id === j.id ? null : j.id)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>
                      {expandedId === j.id ? '▾ DETALLE' : '▸ DETALLE'}
                    </button>
                  )}
                  <button onClick={() => setModal(j)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>{canWrite ? 'EDITAR' : 'VER'}</button>
                  {canWrite && <button onClick={() => setConfirmDelete(j)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>BORRAR</button>}
                </div>
              </div>
              {expandedId === j.id && j.job_type !== 'integrity_check' && (
                <div style={{ marginTop: 10, border: '1px solid var(--border)' }}>
                  <ExclusionsDetail job={j} />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg)' }}>
                {['Nombre','Servidor','Tipo','Schedule','Paths / DB','Último Run','',''].map((h,i) => (
                  <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j, i) => {
                const showInitialLoad = ['pending', 'in_progress'].includes(j.initial_load_state);
                return (
                <Fragment key={j.id}>
                <tr style={{ borderBottom: (i < jobs.length - 1 || expandedId === j.id || showInitialLoad) ? '1px solid var(--border)' : 'none' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                    {j.name}
                    {j.local_repo_path && j.local_repo_mode === 'bootstrap' && !j.local_repo_bootstrapped && (
                      <span style={{ marginLeft: 6, padding: '2px 6px', fontSize: 9, fontFamily: 'var(--mono)', letterSpacing: 1, borderRadius: 2, background: 'rgba(255,209,102,0.15)', color: 'var(--yellow)', verticalAlign: 'middle' }}>⟳ BOOTSTRAP PENDIENTE</span>
                    )}
                    {j.local_repo_path && j.local_repo_mode === 'bootstrap' && j.local_repo_bootstrapped && (
                      <span style={{ marginLeft: 6, padding: '2px 6px', fontSize: 9, fontFamily: 'var(--mono)', letterSpacing: 1, borderRadius: 2, background: 'rgba(0,229,160,0.15)', color: 'var(--green)', verticalAlign: 'middle' }}>✓ DIRECTO A WASABI</span>
                    )}
                    {j.local_repo_path && j.local_repo_mode !== 'bootstrap' && (
                      <span style={{ marginLeft: 6, padding: '2px 6px', fontSize: 9, fontFamily: 'var(--mono)', letterSpacing: 1, borderRadius: 2, background: 'rgba(0,229,160,0.15)', color: 'var(--green)', verticalAlign: 'middle' }}>LOCAL+WAS</span>
                    )}
                    <JobHoneypotProtection jobType={j.job_type} status={j.honeypot_status} jobId={j.id} agentVersion={servers.find(s => s.id === j.server_id)?.agent_version} running={j.job_running} onChanged={() => load(true)} />
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--muted)', fontSize: 12, fontFamily: 'var(--mono)' }}>{j._serverName}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)', borderRadius: 2, background: 'rgba(0,212,255,0.12)', color: 'var(--accent)' }}>{(j.job_type||'').toUpperCase()}</span>
                  </td>
                  <td style={{ padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}
                      title={j.schedule || ''}>{formatCron(j.schedule)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 11, color: 'var(--muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {jobSummary(j)}
                  </td>
                  <td style={{ padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 11 }}>
                    {j.last_run_at ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ color: 'var(--muted)' }} title={j.last_run_at}>{formatDateTime(j.last_run_at, servers.find(s => s.id === j.server_id)?.timezone || DEFAULT_TZ)}</span>
                        <Badge s={j.last_run_status} />
                      </div>
                    ) : (
                      <span style={{ color: 'var(--muted)' }}>Sin runs</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div onClick={canWrite ? () => handleToggle(j) : undefined} title={canWrite ? undefined : 'Lo gestiona tu proveedor'} style={{ width: 36, height: 20, borderRadius: 10, cursor: canWrite ? 'pointer' : 'default', opacity: canWrite ? 1 : 0.6, background: j.enabled ? 'var(--accent)' : 'var(--border)', position: 'relative', display: 'inline-block' }}>
                      <div style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: 'white', left: j.enabled ? 18 : 2, transition: 'left 0.2s' }} />
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', display: 'flex', gap: 4 }}>
                    <button onClick={() => handleTrigger(j.id)} disabled={triggering === j.id} style={{ background: 'transparent', border: '1px solid var(--green)', color: 'var(--green)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', opacity: triggering === j.id ? 0.5 : 1 }}>
                      {triggering === j.id ? '...' : '▶ EJECUTAR'}
                    </button>
                    {j.job_type !== 'integrity_check' && !hideConfig && (
                      <button onClick={() => setExpandedId(id => id === j.id ? null : j.id)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>
                        {expandedId === j.id ? '▾ DETALLE' : '▸ DETALLE'}
                      </button>
                    )}
                    <button onClick={() => setModal(j)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>{canWrite ? 'EDITAR' : 'VER'}</button>
                    {canWrite && <button onClick={() => setConfirmDelete(j)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>BORRAR</button>}
                  </td>
                </tr>
                {showInitialLoad && (
                  <tr style={{ borderBottom: (i < jobs.length - 1 || expandedId === j.id) ? '1px solid var(--border)' : 'none' }}>
                    <td colSpan={8} style={{ padding: '0 16px 12px' }}>
                      <InitialLoadProgress j={j} />
                    </td>
                  </tr>
                )}
                {expandedId === j.id && j.job_type !== 'integrity_check' && (
                  <tr style={{ borderBottom: i < jobs.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <td colSpan={8} style={{ padding: 0 }}>
                      <ExclusionsDetail job={j} />
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
